import axios from 'axios';
import toast from 'react-hot-toast';
import { obfuscate } from '../utils/obfuscate';

// In production, use relative URL since frontend and backend are on same domain
// In development, use localhost:3001
function normalizeBuildTimeApiUrl(raw) {
  const s = String(raw || '').trim().replace(/\/+$/, '');
  if (!s) return null;
  if (s.startsWith('/')) return s;
  try {
    const u = new URL(s);
    const path = (u.pathname || '/').replace(/\/+$/, '') || '/';
    // Common misconfig: REACT_APP_API_URL=https://app.up.railway.app (no /api) → POST /auth/login misses /api prefix
    if (path === '/') {
      u.pathname = '/api';
      return u.href.replace(/\/+$/, '');
    }
    return s;
  } catch {
    return s;
  }
}

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return normalizeBuildTimeApiUrl(process.env.REACT_APP_API_URL);
  }
  // In production (when not on localhost), use relative URL
  if (process.env.NODE_ENV === 'production' || window.location.hostname !== 'localhost') {
    return '/api';
  }
  // In development, use localhost
  return 'http://localhost:3001/api';
};

const API_BASE_URL = getApiBaseUrl();

/**
 * Origin of the backend that serves Express + Socket.IO.
 * When REACT_APP_API_URL points at a different Railway service than the static site,
 * window.location.origin is wrong for sockets — must match the API host.
 */
export function getSocketOrigin() {
  if (!API_BASE_URL) {
    return typeof window !== 'undefined' ? window.location.origin : '';
  }
  if (API_BASE_URL.startsWith('/')) {
    return typeof window !== 'undefined' ? window.location.origin : '';
  }
  try {
    return new URL(API_BASE_URL).origin;
  } catch {
    return typeof window !== 'undefined' ? window.location.origin : '';
  }
}

// 0 = no timeout (large scraper payloads, dataset deletes, slow /accounts, etc.)
const API_TIMEOUT_MS = parseInt(process.env.REACT_APP_API_TIMEOUT_MS || '0', 10);
const resolvedTimeout = Number.isFinite(API_TIMEOUT_MS) && API_TIMEOUT_MS >= 0 ? API_TIMEOUT_MS : 0;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: resolvedTimeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // If superadmin is viewing-as a user, use their delegate token for all requests
    // EXCEPT for admin-scoped routes that must stay superadmin-authenticated:
    //   /admin/orbex  — Orbex license management
    //   /admin/users  — user list / login-token (needed by the switcher itself)
    const delegateToken = window.__viewingAsToken;
    const url = config.url || '';
    const keepSuperadminToken = url.includes('/admin/orbex') || url.includes('/admin/users');
    const token = (delegateToken && !keepSuperadminToken)
      ? delegateToken
      : localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Obfuscate request body for POST, PUT, PATCH requests
    // Skip obfuscation for FormData (multipart/form-data) - it can't be obfuscated
    const isFormData = config.data instanceof FormData;
    
    if (['post', 'put', 'patch'].includes(config.method?.toLowerCase()) && !isFormData) {
      // Skip obfuscation for certain endpoints that need readable data
      const skipObfuscation = [
        '/auth/login',
        '/auth/register',
        '/auth/verify-pin',
        // Backend accepts plain JSON for these routes (see deobfuscate middleware)
        '/account-update-flow',
      ];
      
      const shouldObfuscate = !skipObfuscation.some(endpoint => 
        config.url?.includes(endpoint)
      );
      
      if (shouldObfuscate) {
        try {
          // If config.data is undefined, use empty object
          const dataToObfuscate = config.data !== undefined ? config.data : {};
          config.data = {
            'nice-try': obfuscate(dataToObfuscate)
          };
        } catch (error) {
          console.error('Error obfuscating request data:', error);
          return Promise.reject(new Error('Failed to obfuscate request data'));
        }
      }
    }
    
    // For FormData, remove Content-Type header to let browser set it with boundary
    if (isFormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect for login attempts - let the login handler show the error
      const isLoginRequest = error.config?.url?.includes('/auth/login') || error.config?.url?.includes('/auth/verify-pin');
      if (!isLoginRequest) {
        localStorage.removeItem('token');
        window.location.href = '/';
      }
    } else if (error.response?.status === 503) {
      // DB warming / requireDBConnection — never treat as logged-out (AuthContext retries verify)
      return Promise.reject(error);
    } else if (error.response?.status === 429) {
      // Rate limit error - don't logout, just show warning
      // This shouldn't happen since we removed backend rate limiting,
      // but handle gracefully if it does occur
      const isLoginRequest = error.config?.url?.includes('/auth/login') || error.config?.url?.includes('/auth/verify-pin');
      if (!isLoginRequest) {
        // Show toast but don't redirect or logout
        toast.error('Too many requests. Please wait a moment...', {
          duration: 3000,
        });
      }
      // Return error so caller can handle retry
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

export { api };

/**
 * Download a binary file from an authenticated admin endpoint (zip, etc.).
 * Uses fetch + Bearer token because axios doesn't handle blob downloads cleanly.
 */
export async function downloadAuthenticated(relativePath, fallbackFilename = 'download.zip') {
  const token = localStorage.getItem('token');
  const base = API_BASE_URL.replace(/\/+$/, '');
  const path = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
  const url = `${base}${path}`;

  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  if (!res.ok) {
    let detail = res.statusText || 'Download failed';
    try {
      const payload = await res.json();
      if (payload?.error) detail = payload.error;
    } catch (_) { /* not JSON */ }
    throw new Error(detail);
  }

  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const match = /filename="([^"]+)"/i.exec(cd);
  const filename = match?.[1] || fallbackFilename;

  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}
